/**
 * Pause notifications — maintenance windows, in plain words. While a pause is in force, matching
 * alerts are still RECORDED (alert board, System alerts list, reports) but nobody is emailed or
 * messaged and nothing escalates. When it ends, a system problem that is still open is sent then.
 *
 *   list     "Paused now" (with "End now"), "Coming up", "Earlier"
 *   editor   three questions — why? which alerts? when? — and the sentence on top
 *
 * The site's own page and the cloud's page for a site (same component; the cloud's API proxies).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, PauseCircleIcon, PencilSquareIcon, PlusIcon, StopCircleIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import { Callout } from '../common/Callout';
import { aaInputCls, aaLabelCls } from './AlertActionsControls';
import { Choice, MultiPick, Step } from './RoutingPolicies';
import ChangeHistory from './ChangeHistory';
import {
  SYSTEM_ALERT_KINDS,
  alertActionsErrorText,
  type AlertSilence,
  type PolicyChange,
  type PolicyDirectory,
  type SilencesApi,
  type SystemAlertKind,
} from './alertActionsModel';

export interface SilencesPanelProps {
  api: SilencesApi;
  canEdit: boolean;
  /** Who changed pauses (the routing history). */
  history?: (opts?: { entity?: 'policy' | 'silence'; limit?: number }) => Promise<PolicyChange[]>;
  eventTypeLabel?: (type: string) => string;
}

const REASONS = ['Planned maintenance', 'Camera being moved', 'Fire drill', 'Power work', 'Network work'];
const LENGTHS = [
  { min: 30, label: '30 min' }, { min: 60, label: '1 hour' }, { min: 120, label: '2 hours' }, { min: 240, label: '4 hours' },
  { min: 480, label: '8 hours' }, { min: 1440, label: '1 day' },
];

const when = (iso: string) => new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
/** A Date as a datetime-local value, in the browser's time. */
const localInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const listText = (items: string[], max = 3) => {
  if (!items.length) return '';
  const shown = items.slice(0, max);
  const more = items.length - shown.length;
  if (more > 0) return `${shown.join(', ')} and ${more} more`;
  return shown.length > 1 ? `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}` : shown[0];
};

interface Names { camera: Map<string, string>; group: Map<string, string>; rule: Map<string, string> }

/**
 * "AI alerts from Fire Detected on Store Room 2" / "every alert" / "system problems (…)". A pause
 * limited to cameras only ever holds back those cameras' alerts — for system problems, that is
 * their camera-offline alerts (the backend's silences.matchFor).
 */
function whatText(s: Pick<AlertSilence, 'kind' | 'camera_ids' | 'group_ids' | 'rule_ids' | 'system_kinds'>, names: Names): string {
  const cams = [...s.group_ids.map((id) => `${names.group.get(id) || 'a removed group'} cameras`), ...s.camera_ids.map((id) => names.camera.get(id) || 'a removed camera')];
  const where = cams.length ? ` on ${listText(cams)}` : '';
  const kinds = s.system_kinds.map((k) => SYSTEM_ALERT_KINDS.find((x) => x.value === k)?.label.toLowerCase() || k);
  const ai = s.rule_ids.length ? `alerts from ${listText(s.rule_ids.map((id) => names.rule.get(id) || 'a removed rule'))}` : 'AI alerts';
  const sys = cams.length ? 'camera-offline alerts' : kinds.length ? `system problems (${listText(kinds, 5)})` : 'system problems';
  if (s.kind === 'ai') return `${ai}${where}`;
  if (s.kind === 'system') return `${sys}${where}`;
  if (!cams.length && !s.rule_ids.length && !kinds.length) return 'every alert';
  return cams.length ? `${ai} and ${sys}${where}` : `${ai}, and ${sys}`;
}

const STATE_TEXT: Record<AlertSilence['state'], string> = { active: 'Paused now', upcoming: 'Coming up', ended: 'Ended', cancelled: 'Ended early' };

// ─── Editor ─────────────────────────────────────────────────────────

interface Draft {
  reason: string;
  kind: AlertSilence['kind'];
  scope: 'all' | 'groups' | 'cameras';
  camera_ids: string[];
  group_ids: string[];
  byRule: boolean;
  rule_ids: string[];
  system_kinds: SystemAlertKind[];
  startNow: boolean;
  starts: string;
  lengthMin: number | null;
  ends: string;
}

function draftOf(s: AlertSilence | null): Draft {
  const now = new Date();
  if (!s) {
    return {
      reason: '', kind: 'all', scope: 'all', camera_ids: [], group_ids: [], byRule: false, rule_ids: [], system_kinds: [],
      startNow: true, starts: localInput(now), lengthMin: 120, ends: localInput(new Date(now.getTime() + 120 * 60000)),
    };
  }
  return {
    reason: s.reason, kind: s.kind,
    scope: s.group_ids.length ? 'groups' : s.camera_ids.length ? 'cameras' : 'all',
    camera_ids: s.camera_ids, group_ids: s.group_ids, byRule: s.rule_ids.length > 0, rule_ids: s.rule_ids, system_kinds: s.system_kinds,
    startNow: s.state === 'active', starts: localInput(new Date(s.starts_at)), lengthMin: null, ends: localInput(new Date(s.ends_at)),
  };
}

function SilenceEditor({ open, initial, dir, names, api, onClose, onSaved }: {
  open: boolean; initial: AlertSilence | null; dir: PolicyDirectory | null; names: Names; api: SilencesApi;
  onClose: () => void; onSaved: (s: AlertSilence) => void;
}) {
  const [d, setD] = useState<Draft>(() => draftOf(initial));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setD(draftOf(initial)); setError(''); } }, [open, initial]);
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const running = initial?.state === 'active';

  const start = d.startNow ? new Date() : new Date(d.starts);
  const end = d.lengthMin ? new Date(start.getTime() + d.lengthMin * 60000) : new Date(d.ends);
  const body = {
    reason: d.reason.trim(),
    kind: d.kind,
    camera_ids: d.kind !== 'system' || d.system_kinds.includes('camera') ? (d.scope === 'cameras' ? d.camera_ids : []) : [],
    group_ids: d.kind !== 'system' || d.system_kinds.includes('camera') ? (d.scope === 'groups' ? d.group_ids : []) : [],
    rule_ids: d.kind !== 'system' && d.byRule ? d.rule_ids : [],
    system_kinds: d.kind !== 'ai' ? d.system_kinds : [],
  };
  const sentence = (
    <>
      Notifications for <b>{whatText(body as AlertSilence, names)}</b> are paused
      {d.startNow || running ? ' from now' : Number.isNaN(start.getTime()) ? '' : <> from <b>{when(start.toISOString())}</b></>}
      {!Number.isNaN(end.getTime()) && <> until <b>{when(end.toISOString())}</b></>}. Alerts are still recorded.
    </>
  );

  const save = async () => {
    if (!body.reason) return setError('Say why — e.g. "Planned maintenance"');
    if (d.scope === 'cameras' && !d.camera_ids.length && (d.kind !== 'system')) return setError('Pick at least one camera');
    if (d.scope === 'groups' && !d.group_ids.length && (d.kind !== 'system')) return setError('Pick at least one camera group');
    if (d.byRule && !d.rule_ids.length && d.kind !== 'system') return setError('Pick at least one alert rule');
    if (Number.isNaN(end.getTime()) || end <= start) return setError('The end must come after the start');
    setBusy(true);
    setError('');
    try {
      onSaved(await api.save(initial?.id || null, {
        ...body,
        ...(running ? {} : { starts_at: start.toISOString() }),
        ends_at: end.toISOString(),
      }));
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not save the pause'));
    } finally {
      setBusy(false);
    }
  };

  const cameraOptions = (dir?.cameras || []).map((c) => ({ id: c.id, label: c.name }));
  const groupOptions = (dir?.groups || []).map((g) => ({ id: g.id, label: g.name }));
  const ruleOptions = (dir?.rules || []).map((r) => ({ id: r.id, label: r.name || r.event_type, hint: r.is_active ? undefined : '(off)' }));
  const showCameras = d.kind !== 'system' || d.system_kinds.includes('camera');

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={initial ? 'Change the pause' : 'Pause notifications'}
      subtitle="Alerts are still recorded — only the emails and messages stop."
      footer={(
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1 text-[12px] text-destructive">{error}</span>
          <button type="button" onClick={onClose} className="rounded-lg border border-border px-3 py-1.5 text-[13px] text-foreground hover:bg-muted">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="rounded-lg bg-brand px-4 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50">
            {busy ? 'Saving…' : initial ? 'Save' : 'Pause notifications'}
          </button>
        </div>
      )}
    >
      <div className="space-y-5 p-5">
        <div className="rounded-xl border border-border bg-muted px-4 py-3 text-[13px] leading-relaxed text-foreground">{sentence}</div>

        <Step n={1} title="Why?" hint="Shown to everyone, and in the history.">
          <input value={d.reason} onChange={(e) => set({ reason: e.target.value })} placeholder="e.g. Planned maintenance" className={`${aaInputCls} max-w-md`} maxLength={255} />
          <div className="flex flex-wrap gap-1.5">
            {REASONS.map((r) => <Choice key={r} on={d.reason === r} onClick={() => set({ reason: r })}>{r}</Choice>)}
          </div>
        </Step>

        <Step n={2} title="Which alerts?">
          <div className="flex flex-wrap gap-1.5">
            <Choice on={d.kind === 'all'} onClick={() => set({ kind: 'all' })}>Every alert</Choice>
            <Choice on={d.kind === 'ai'} onClick={() => set({ kind: 'ai' })}>AI alerts only</Choice>
            <Choice on={d.kind === 'system'} onClick={() => set({ kind: 'system' })}>System problems only</Choice>
          </div>
          {d.kind !== 'ai' && (
            <div className="space-y-1">
              <p className={aaLabelCls}>System problems {d.kind === 'all' && '(leave all unticked for every kind)'}</p>
              <div className="flex flex-wrap gap-1.5">
                {SYSTEM_ALERT_KINDS.map((k) => (
                  <Choice key={k.value} on={d.system_kinds.includes(k.value)} onClick={() => set({ system_kinds: d.system_kinds.includes(k.value) ? d.system_kinds.filter((x) => x !== k.value) : [...d.system_kinds, k.value] })}>
                    {k.label}
                  </Choice>
                ))}
              </div>
            </div>
          )}
          {showCameras && (
            <div className="space-y-1.5">
              <p className={aaLabelCls}>Cameras</p>
              <div className="flex flex-wrap gap-1.5">
                <Choice on={d.scope === 'all'} onClick={() => set({ scope: 'all' })}>All cameras</Choice>
                <Choice on={d.scope === 'groups'} onClick={() => set({ scope: 'groups' })}>A group of cameras</Choice>
                <Choice on={d.scope === 'cameras'} onClick={() => set({ scope: 'cameras' })}>Some cameras</Choice>
              </div>
              {d.scope === 'groups' && <MultiPick options={groupOptions} selected={d.group_ids} onChange={(group_ids) => set({ group_ids })} placeholder="Find a camera group" empty="No camera groups" />}
              {d.scope === 'cameras' && <MultiPick options={cameraOptions} selected={d.camera_ids} onChange={(camera_ids) => set({ camera_ids })} placeholder="Find a camera" empty="No cameras" />}
              {d.kind === 'all' && d.scope !== 'all' && <p className="text-[12px] text-muted-foreground">Service, CPU, memory and disk problems are not tied to cameras, so a camera pause never holds them back.</p>}
            </div>
          )}
          {d.kind !== 'system' && (
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-[13px] text-foreground">
                <input type="checkbox" checked={d.byRule} onChange={(e) => set({ byRule: e.target.checked })} className="accent-brand" />
                Only alerts from some rules
              </label>
              {d.byRule && <MultiPick options={ruleOptions} selected={d.rule_ids} onChange={(rule_ids) => set({ rule_ids })} placeholder="Find an alert rule" empty="No alert rules" />}
            </div>
          )}
        </Step>

        <Step n={3} title="When?">
          {!running && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Choice on={d.startNow} onClick={() => set({ startNow: true })}>Start now</Choice>
              <Choice on={!d.startNow} onClick={() => set({ startNow: false })}>Start later</Choice>
              {!d.startNow && <input type="datetime-local" value={d.starts} onChange={(e) => set({ starts: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-1 text-[13px] text-foreground" />}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[13px] text-muted-foreground">{running ? 'Ends' : 'For'}</span>
            {!running && LENGTHS.map((l) => <Choice key={l.min} on={d.lengthMin === l.min} onClick={() => set({ lengthMin: l.min })}>{l.label}</Choice>)}
            <Choice on={d.lengthMin === null} onClick={() => set({ lengthMin: null })}>{running ? 'at' : 'Until…'}</Choice>
            {d.lengthMin === null && <input type="datetime-local" value={d.ends} onChange={(e) => set({ ends: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-1 text-[13px] text-foreground" />}
          </div>
          <p className="text-[12px] text-muted-foreground">At most 31 days. A system problem still open when the pause ends is sent then.</p>
        </Step>

      </div>
    </Modal>
  );
}

// ─── The list ───────────────────────────────────────────────────────

export default function SilencesPanel({ api, canEdit, history }: SilencesPanelProps) {
  const [rows, setRows] = useState<AlertSilence[] | null>(null);
  const [dir, setDir] = useState<PolicyDirectory | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<AlertSilence | null | 'new'>(null);
  const [ending, setEnding] = useState<AlertSilence | null>(null);
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(async () => {
    setError('');
    const [list, directory] = await Promise.allSettled([api.list(), api.directory()]);
    if (list.status === 'fulfilled') setRows(list.value);
    else { setRows([]); setError(alertActionsErrorText(list.reason, 'Could not read the pauses')); }
    if (directory.status === 'fulfilled') setDir(directory.value);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const names: Names = useMemo(() => ({
    camera: new Map((dir?.cameras || []).map((c) => [c.id, c.name])),
    group: new Map((dir?.groups || []).map((g) => [g.id, g.name])),
    rule: new Map((dir?.rules || []).map((r) => [r.id, r.name || r.event_type])),
  }), [dir]);

  const active = (rows || []).filter((r) => r.state === 'active');
  const upcoming = (rows || []).filter((r) => r.state === 'upcoming');
  const past = (rows || []).filter((r) => r.state === 'ended' || r.state === 'cancelled');

  const card = (s: AlertSilence) => (
    <article key={s.id} className={`flex gap-3 rounded-xl border bg-card px-4 py-3 ${s.state === 'active' ? 'border-amber-500' : 'border-border'} ${s.state === 'ended' || s.state === 'cancelled' ? 'opacity-70' : ''}`}>
      <PauseCircleIcon className={`mt-0.5 h-5 w-5 shrink-0 ${s.state === 'active' ? 'text-amber-500' : 'text-muted-foreground'}`} />
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-foreground">{s.reason} <span className="ml-1 text-[12px] font-normal text-muted-foreground">{STATE_TEXT[s.state]}</span></p>
        <p className="text-[13px] leading-relaxed text-foreground">
          Notifications for <b>{whatText(s, names)}</b> {s.state === 'upcoming' ? 'will be' : s.state === 'active' ? 'are' : 'were'} paused
          {' '}from <b>{when(s.starts_at)}</b> until <b>{when(s.cancelled_at || s.ends_at)}</b>.
        </p>
        <p className="text-[12px] text-muted-foreground">
          {s.created_by ? `By ${s.created_by}` : ''}{s.cancelled_by ? ` · ended early by ${s.cancelled_by}` : ''}
        </p>
      </div>
      {canEdit && (s.state === 'active' || s.state === 'upcoming') && (
        <div className="flex shrink-0 items-start gap-1">
          <button type="button" onClick={() => setEditing(s)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-[12px] text-foreground hover:bg-muted">
            <PencilSquareIcon className="h-4 w-4" /> Change
          </button>
          <button type="button" onClick={() => setEnding(s)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-[12px] text-foreground hover:bg-muted">
            <StopCircleIcon className="h-4 w-4" /> {s.state === 'active' ? 'End now' : 'Cancel'}
          </button>
        </div>
      )}
    </article>
  );

  return (
    <div className="space-y-3">
      <ul className="space-y-1 rounded-xl border border-border bg-card px-4 py-3 text-[13px] text-foreground">
        <li>• Planned work, a drill, a camera being moved? <b>Pause notifications</b> so nobody is emailed or messaged for it.</li>
        <li>• Alerts are still <b>recorded</b> — on the alert board, the System alerts list and the reports.</li>
        <li>• Nothing escalates while paused. A system problem still open when the pause ends is sent then.</li>
      </ul>
      {error && <Callout tone="danger">{error}</Callout>}

      <div className="flex flex-wrap items-center gap-2">
        <h3 className="min-w-0 flex-1 text-[15px] font-semibold text-foreground">Paused notifications</h3>
        <button type="button" onClick={load} className="rounded-lg border border-border p-1.5 text-muted-foreground hover:bg-muted" title="Reload">
          <ArrowPathIcon className="h-4 w-4" />
        </button>
        {canEdit && (
          <button type="button" onClick={() => setEditing('new')} className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90">
            <PlusIcon className="h-4 w-4" /> Pause notifications
          </button>
        )}
      </div>

      {rows === null && <p className="text-[13px] text-muted-foreground">Loading…</p>}
      {rows && !active.length && !upcoming.length && (
        <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-[13px] text-muted-foreground">Nothing is paused — every alert is sent as set in Routing.</p>
      )}
      <div className="space-y-2">{active.map(card)}{upcoming.map(card)}</div>

      {past.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowPast(!showPast)} className="text-[12px] font-medium text-brand hover:underline">
            {showPast ? 'Hide earlier pauses' : `Show earlier pauses (${past.length})`}
          </button>
          {showPast && <div className="mt-2 space-y-2">{past.map(card)}</div>}
        </div>
      )}

      {history && <ChangeHistory load={history} entity="silence" title="Who paused what" />}

      <SilenceEditor
        open={editing !== null}
        initial={editing === 'new' ? null : editing}
        dir={dir}
        names={names}
        api={api}
        onClose={() => setEditing(null)}
        onSaved={(saved) => {
          setRows((list) => [saved, ...(list || []).filter((x) => x.id !== saved.id)]);
          setEditing(null);
        }}
      />
      <ConfirmDialog
        open={!!ending}
        title={ending?.state === 'active' ? 'End this pause now?' : 'Cancel this pause?'}
        message={ending ? `"${ending.reason}" — notifications for these alerts are sent again straight away.` : ''}
        confirmLabel={ending?.state === 'active' ? 'End now' : 'Cancel pause'}
        onCancel={() => setEnding(null)}
        onConfirm={async () => {
          if (!ending) return;
          try {
            const saved = await api.cancel(ending.id);
            setRows((list) => (list || []).map((x) => (x.id === saved.id ? saved : x)));
          } catch (err) {
            setError(alertActionsErrorText(err, 'Could not end the pause'));
          }
          setEnding(null);
        }}
      />
    </div>
  );
}
